import { components } from "@/api-schema";
import { RelocationOrderStatus } from "@/api-schema-enums";
import { RelocationOrderFlowAction } from "@/common/api/relocation-order-flow/_helpers/resolve-relocation-order-flow-path";
import { useRelocationOrderFlowMutation } from "@/common/api/relocation-order-flow/use-relocation-order-flow.mutation";
import { useRelocationOrderPickupLocationsQuery } from "@/common/api/relocation-order/use-relocation-order-pickup-locations.query";
import { useRelocationOrderSearchQuery } from "@/common/api/relocation-order/use-relocation-order-search.query";
import { showApiErrorToast } from "@/common/helpers/show-api-error-toast";
import { cn } from "@/common/helpers/utils";
import { RelocationOrderTable } from "@/components/data-table/predefined/relocation-order-table";
import { DataTableHeightMode } from "@/components/data-table/types";
import { BranchFilter } from "@/components/filter/predefined/branch-filter";
import { BrandFilter } from "@/components/filter/predefined/brand-filter";
import { ModelGenerationFilter } from "@/components/filter/predefined/model-generation-filter";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { userStore } from "@/store/user-store";
import { Check, MapPin, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useSearchParams } from "wouter";
import { useShallow } from "zustand/react/shallow";
import { TransportCompanyAssignmentFallback } from "../_components/transport-company-assignment-fallback";
import { EMPTY_CITY_TABLE_PROPS, resolveCityAddresses } from "../_helpers/resolve-city-addresses";
import { useTransportCompanyAssignment } from "../_hooks/use-transport-company-assignment";
import { CannotPickUpReasonSelector } from "./_components/cannot-pick-up-reason-selector";
import { GeoLocationSelector } from "./_components/geo-location-selector";
import { LoadViewNoLocations } from "./_components/load-view-no-locations";
import { LoadViewSelectLocation } from "./_components/load-view-select-location";
import { LoadViewSkeleton } from "./_components/load-view-skeleton";
import { PickupLocation, PickupLocationType } from "./_components/searchable-location-selector";
import { StatusButton } from "./_components/status-button";
import { VehicleInfoCell } from "./_components/vehicle-info-cell";
import { confirmLoadStatusChanges } from "./_helpers/confirm-load-status-changes";
import { resolveLoadErrorDescription } from "./_helpers/load-error-description";
import { isRejectionStatus } from "./_helpers/load-status";
import { isKnownFilterId, useFitExternalLoadStore } from "./_hooks/use-load-store";

type RelocationOrderBody = components["schemas"]["RelocationOrderSearchBody"];

const FIT_TRUCK_MAX_VEHICLES = 9;
const BRANCH_SEARCH_PARAM = "branch";
const CITY_SEARCH_PARAM = "city";

export function LoadView() {
  const { t } = useTranslation(["fit", "common"]);
  const { user } = userStore();
  const { shouldShowFallback } = useTransportCompanyAssignment();
  const [searchParams, setSearchParams] = useSearchParams();
  const { mutateAsync: transportCompanyPickUpVehicleMutation } = useRelocationOrderFlowMutation(
    RelocationOrderFlowAction.TransportCompanyPickupVehicle,
  );
  const { mutateAsync: transportCompanyRejectVehicleMutation } = useRelocationOrderFlowMutation(
    RelocationOrderFlowAction.TransportCompanyRejectVehicle,
  );

  const {
    statusChanges,
    selectedBranch,
    selectedCity,
    filterValues,
    isConfirming,
    getLoadStatus,
    cycleLoadStatus,
    clearLoadSelections,
    setSelectedBranch,
    setSelectedCity,
    setFilterValue,
    setIsConfirming,
    reset,
  } = useFitExternalLoadStore(
    useShallow((s) => ({
      statusChanges: s.statusChanges,
      selectedBranch: s.selectedBranch,
      selectedCity: s.selectedCity,
      filterValues: s.filterValues,
      isConfirming: s.isConfirming,
      getLoadStatus: s.getLoadStatus,
      cycleLoadStatus: s.cycleLoadStatus,
      clearLoadSelections: s.clearLoadSelections,
      setSelectedBranch: s.setSelectedBranch,
      setSelectedCity: s.setSelectedCity,
      setFilterValue: s.setFilterValue,
      setIsConfirming: s.setIsConfirming,
      reset: s.reset,
    })),
  );

  const canAccessLoadView = !shouldShowFallback;
  const canUseTransportCompanyFlows = canAccessLoadView && !!user?.uuid;

  const pickupLocationsQuery = useRelocationOrderPickupLocationsQuery(undefined, {
    enabled: canAccessLoadView,
  });
  const pickupBranches = useMemo(() => pickupLocationsQuery.data?.branches ?? [], [pickupLocationsQuery.data]);
  const addressGroups = useMemo(() => pickupLocationsQuery.data?.addressGroups ?? [], [pickupLocationsQuery.data]);

  const [isCapacityDialogOpen, setIsCapacityDialogOpen] = useState(false);
  const isLocationHydratedRef = useRef(false);

  const changedItems = useMemo(
    () =>
      Object.values(statusChanges).filter(
        ({ status }) => status === RelocationOrderStatus.TransportCompanyInTransit || isRejectionStatus(status),
      ),
    [statusChanges],
  );

  const changedItemsCount = changedItems.length;

  const pendingLoadCount = useMemo(
    () => changedItems.filter(({ status }) => status === RelocationOrderStatus.TransportCompanyInTransit).length,
    [changedItems],
  );

  const currentTruckLoadBody = useMemo((): RelocationOrderBody => {
    if (!user?.uuid) {
      return {};
    }

    return {
      status: [RelocationOrderStatus.TransportCompanyInTransit],
      pickedUpBy: [user.uuid],
    };
  }, [user?.uuid]);

  const currentTruckLoadQuery = useRelocationOrderSearchQuery({
    options: {
      body: currentTruckLoadBody,
      parameters: {
        page: 1,
        pageSize: FIT_TRUCK_MAX_VEHICLES + 1,
      },
    },
    enabled: canUseTransportCompanyFlows,
  });

  const currentTruckVehicleCount =
    currentTruckLoadQuery.data?.meta?.total ?? currentTruckLoadQuery.data?.data?.length ?? 0;
  const projectedTruckVehicleCount = currentTruckVehicleCount + pendingLoadCount;
  const isCapacityExceeded = pendingLoadCount > 0 && projectedTruckVehicleCount > FIT_TRUCK_MAX_VEHICLES;
  const isCheckingTruckCapacity = pendingLoadCount > 0 && currentTruckLoadQuery.isLoading;

  const syncLocationSearchParams = useCallback(
    (branchId: string | null, cityName: string | null) => {
      setSearchParams((currentParams) => {
        const nextParams = new URLSearchParams(currentParams.toString());

        if (branchId) {
          nextParams.set(BRANCH_SEARCH_PARAM, branchId);
          nextParams.delete(CITY_SEARCH_PARAM);
        } else if (cityName) {
          nextParams.set(CITY_SEARCH_PARAM, cityName);
          nextParams.delete(BRANCH_SEARCH_PARAM);
        } else {
          nextParams.delete(BRANCH_SEARCH_PARAM);
          nextParams.delete(CITY_SEARCH_PARAM);
        }

        return nextParams;
      });
    },
    [setSearchParams],
  );

  const handleLocationChange = useCallback(
    (location: PickupLocation) => {
      const newBranchId = location?.type === PickupLocationType.Branch ? location.uuid : null;
      const newCity = location?.type === PickupLocationType.City ? location.city : null;

      if (selectedBranch !== newBranchId || selectedCity !== newCity) {
        clearLoadSelections();
        setIsCapacityDialogOpen(false);
      }

      if (location?.type === PickupLocationType.Branch) {
        setSelectedBranch(location.uuid);
      } else if (location?.type === PickupLocationType.City) {
        setSelectedCity(location.city);
      } else {
        setSelectedBranch(null);
        setSelectedCity(null);
      }
    },
    [clearLoadSelections, selectedBranch, selectedCity, setSelectedBranch, setSelectedCity],
  );

  const handleConfirmSelection = useCallback(async () => {
    if (changedItems.length === 0 || isConfirming) return;

    if (isCheckingTruckCapacity) return;

    if (isCapacityExceeded) {
      setIsCapacityDialogOpen(true);
      return;
    }

    if (!user?.uuid) {
      toast.error(t("fit:UserError"), {
        description: t("fit:UserInformationUnavailablePleaseReload"),
      });
      return;
    }

    setIsConfirming(true);
    try {
      await confirmLoadStatusChanges(
        changedItems,
        transportCompanyPickUpVehicleMutation,
        transportCompanyRejectVehicleMutation,
      );

      toast.success(t("fit:ConfirmedSuccessfully"), {
        description: t("fit:VehiclesSuccessfullyUpdated", { count: changedItems.length }),
      });

      clearLoadSelections();
    } catch (error: unknown) {
      showApiErrorToast(t("fit:UpdateFailed"), error, {
        description: resolveLoadErrorDescription(error, t("fit:VehicleStatusCouldNotBeUpdatedPleaseTryAgain"), t),
      });
    } finally {
      setIsConfirming(false);
    }
  }, [
    changedItems,
    transportCompanyPickUpVehicleMutation,
    transportCompanyRejectVehicleMutation,
    isCapacityExceeded,
    isCheckingTruckCapacity,
    isConfirming,
    clearLoadSelections,
    setIsConfirming,
    t,
    user?.uuid,
  ]);

  const handleConfirmButtonClick = useCallback(() => {
    if (isCapacityExceeded) {
      setIsCapacityDialogOpen(true);
      return;
    }

    void handleConfirmSelection();
  }, [handleConfirmSelection, isCapacityExceeded]);

  const filters = useMemo(
    () => [
      new BranchFilter().setLabelOverrides({
        labelSingular: t("fit:DestinationBranch"),
        labelPlural: t("fit:DestinationBranches"),
        placeholder: t("fit:SelectDestinationBranches"),
        searchPlaceholder: t("fit:SearchDestinationBranches"),
        emptyText: t("fit:NoDestinationBranchesFound"),
      }),
      new BrandFilter(),
      new ModelGenerationFilter({ body: { brand: filterValues.brands || [] } }),
    ],
    [filterValues.brands, t],
  );

  const handleFilterChange = useCallback(
    (filterId: string, values: string[]) => {
      if (!isKnownFilterId(filterId)) return;
      setFilterValue(filterId, values);
    },
    [setFilterValue],
  );

  const relocationOrderBody = useMemo(() => {
    if (!selectedBranch && !selectedCity) {
      return null;
    }

    const cityAddresses = resolveCityAddresses(addressGroups, selectedCity);

    if (selectedCity && !cityAddresses) {
      return null;
    }

    const baseBody: RelocationOrderBody = {
      status: [RelocationOrderStatus.BranchAwaitingPickup, RelocationOrderStatus.TransportCompanyRestAtLayover],
      ...(selectedBranch ? { currentBranch: [selectedBranch] } : {}),
      ...(cityAddresses ? { currentAddress: cityAddresses } : {}),
      ...(filterValues.branches?.length > 0 && {
        destinationBranch: filterValues.branches,
      }),
      ...(filterValues.brands?.length > 0 && {
        vehicleBrand: filterValues.brands,
      }),
      ...(filterValues.modelGenerations?.length > 0 && {
        vehicleModelGeneration: filterValues.modelGenerations,
      }),
    };

    return baseBody;
  }, [selectedBranch, selectedCity, addressGroups, filterValues]);

  const mobileOptimizedColumns = useMemo(
    () => [
      {
        id: "vehicleInfo",
        size: 1000,
        enableSorting: false,
        header: t("fit:VehicleAndDestination"),
        cell: ({ row }) => <VehicleInfoCell row={row} />,
      },
      {
        id: "loadStatus",
        size: 80,
        enableSorting: false,
        header: t("fit:Status"),
        cell: ({ row }) => {
          const currentStatus = getLoadStatus(row.original.uuid);
          return <StatusButton currentStatus={currentStatus} onStatusCycle={() => cycleLoadStatus(row.original)} />;
        },
      },
    ],
    [getLoadStatus, cycleLoadStatus, t],
  );

  useEffect(() => {
    if (shouldShowFallback || !canAccessLoadView || isLocationHydratedRef.current || pickupLocationsQuery.isLoading) {
      return;
    }

    const branchParam = searchParams.get(BRANCH_SEARCH_PARAM);
    const cityParam = searchParams.get(CITY_SEARCH_PARAM);

    if (branchParam) {
      if (pickupBranches.some((b) => b.uuid === branchParam)) {
        setSelectedBranch(branchParam);
      } else {
        syncLocationSearchParams(null, null);
      }
    } else if (cityParam) {
      if (addressGroups.some((g) => g.city === cityParam)) {
        setSelectedCity(cityParam);
      } else {
        syncLocationSearchParams(null, null);
      }
    }

    isLocationHydratedRef.current = true;
  }, [
    shouldShowFallback,
    canAccessLoadView,
    pickupLocationsQuery.isLoading,
    searchParams,
    pickupBranches,
    addressGroups,
    setSelectedBranch,
    setSelectedCity,
    syncLocationSearchParams,
  ]);

  useEffect(() => {
    return () => reset();
  }, [reset]);

  useEffect(() => {
    if (shouldShowFallback || !canAccessLoadView || !isLocationHydratedRef.current) {
      return;
    }

    syncLocationSearchParams(selectedBranch, selectedCity);
  }, [shouldShowFallback, canAccessLoadView, selectedBranch, selectedCity, syncLocationSearchParams]);

  if (shouldShowFallback) {
    return (
      <CenteredLayout
        variant={CenteredLayoutVariant.Table}
        title={t("common:Load")}
        subTitle={t("fit:LoadDescription")}
      >
        <TransportCompanyAssignmentFallback />
      </CenteredLayout>
    );
  }

  if (pickupLocationsQuery.isLoading) {
    return <LoadViewSkeleton />;
  }

  if (pickupBranches.length === 0 && addressGroups.length === 0 && !selectedBranch && !selectedCity) {
    return <LoadViewNoLocations />;
  }

  return (
    <CenteredLayout variant={CenteredLayoutVariant.Table} title={t("common:Load")} subTitle={t("fit:LoadDescription")}>
      <div className="flex min-h-0 flex-1 flex-col">
        <GeoLocationSelector
          className="mb-4"
          pickupBranches={pickupBranches}
          addressGroups={addressGroups}
          selectedBranch={selectedBranch}
          selectedCity={selectedCity}
          onLocationChange={handleLocationChange}
          isLoading={pickupLocationsQuery.isPending}
        />

        <div className="flex min-h-0 flex-1 flex-col">
          {!selectedBranch && !selectedCity ? (
            <LoadViewSelectLocation />
          ) : (
            <RelocationOrderTable
              columns={mobileOptimizedColumns}
              body={relocationOrderBody ?? undefined}
              {...(relocationOrderBody ? {} : EMPTY_CITY_TABLE_PROPS)}
              filters={filters}
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              features={{
                pagination: true,
                search: true,
                multiSelection: false,
                expansion: true,
                hideExpansionButton: true,
              }}
              pageSize={100}
              searchPlaceholder={t("fit:SearchVinBrandOrDestination")}
              noDataIcon={MapPin}
              noDataTitle={t("fit:NoVehiclesToLoad")}
              noDataDescription={t("fit:NoVehiclesToLoadDescription")}
              noResultsTitle={t("fit:NoVehiclesToLoadMatchFilters")}
              noResultsDescription={t("fit:NoVehiclesToLoadMatchFiltersDescription")}
              subComponent={({ row }) => {
                const status = getLoadStatus(row.original.uuid);

                if (isRejectionStatus(status)) {
                  return <CannotPickUpReasonSelector relocationOrder={row.original} />;
                }

                return null;
              }}
              getRowCanExpand={(item) => {
                const uuid = item.uuid;
                const status = getLoadStatus(uuid);
                return isRejectionStatus(status);
              }}
              footerComponent={
                changedItemsCount > 0 ? (
                  <div className="border-border bg-background/95 supports-backdrop-filter:bg-background/90 flex flex-col gap-3 rounded-xl border p-4 shadow-sm backdrop-blur">
                    {pendingLoadCount > 0 && (
                      <div className="grid grid-cols-3 gap-3 text-center">
                        <div className="min-w-0">
                          <p className="text-foreground text-lg leading-none font-semibold tabular-nums">
                            {currentTruckVehicleCount}
                          </p>
                          <p className="text-muted-foreground mt-1 text-[11px] leading-tight sm:text-xs">
                            {t("fit:TruckCapacityLoaded")}
                          </p>
                        </div>

                        <div className="min-w-0">
                          <p className="text-foreground text-lg leading-none font-semibold tabular-nums">
                            {pendingLoadCount}
                          </p>
                          <p className="text-muted-foreground mt-1 text-[11px] leading-tight sm:text-xs">
                            {t("fit:TruckCapacitySelected")}
                          </p>
                        </div>

                        <div className="min-w-0">
                          <p className="text-foreground text-lg leading-none font-semibold tabular-nums">
                            {FIT_TRUCK_MAX_VEHICLES}
                          </p>
                          <p className="text-muted-foreground mt-1 text-[11px] leading-tight sm:text-xs">
                            {t("fit:TruckCapacityLimit")}
                          </p>
                        </div>
                      </div>
                    )}

                    <Button
                      onClick={handleConfirmButtonClick}
                      disabled={isConfirming || isCheckingTruckCapacity}
                      aria-disabled={isCapacityExceeded || undefined}
                      className={cn(
                        "h-12 w-full text-lg font-semibold",
                        isCapacityExceeded &&
                          "border-warning/40 bg-warning/10 text-warning hover:border-warning/40 hover:bg-warning/10 hover:text-warning",
                      )}
                      size={ButtonSize.Lg}
                      variant={isCapacityExceeded ? ButtonVariant.Outlined : ButtonVariant.Filled}
                      colorVariant={isCapacityExceeded ? ButtonColorVariant.Secondary : ButtonColorVariant.Primary}
                    >
                      {isConfirming ? (
                        <>
                          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor className="mr-2" />
                          {t("fit:Confirming")}
                        </>
                      ) : isCheckingTruckCapacity ? (
                        <>
                          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor className="mr-2" />
                          {t("fit:CheckingTruckCapacity")}
                        </>
                      ) : (
                        <>
                          {isCapacityExceeded ? (
                            <TriangleAlert className="mr-2 h-5 w-5" />
                          ) : (
                            <Check className="mr-2 h-5 w-5" />
                          )}
                          {t("fit:ConfirmSelection")} ({changedItemsCount})
                        </>
                      )}
                    </Button>
                  </div>
                ) : null
              }
              heightMode={DataTableHeightMode.Custom}
            />
          )}
        </div>
      </div>

      <AlertDialog open={isCapacityDialogOpen} onOpenChange={setIsCapacityDialogOpen}>
        <AlertDialogContent className="w-[calc(100%-2rem)] sm:w-full">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("fit:TruckCapacityExceededTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("fit:TruckCapacityExceededDescription", {
                max: FIT_TRUCK_MAX_VEHICLES,
                current: currentTruckVehicleCount,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogAction>{t("fit:TruckCapacityDialogAction")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CenteredLayout>
  );
}
